class ProductsTabs extends HTMLElement {
  constructor() {
    super();
    this.onKeydown = this.onKeydown.bind(this);
    this.sliders = [];
  }

  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll("[data-tab-trigger]"));
    this.panels = Array.from(this.querySelectorAll("[data-tab-panel]"));

    if (!this.triggers.length || !this.panels.length) return;

    this.initSliders();

    this.triggers.forEach((trigger, index) => {
      trigger.addEventListener("click", () => this.activateTab(index));
      trigger.addEventListener("keydown", this.onKeydown);
    });

    this.updateVisibleSlider();
  }

  initSliders() {
    if (typeof Swiper === "undefined") return;

    this.sliders = this.panels.map((panel) => {
      const slider = panel.querySelector("[data-tab-slider]");
      const viewport = slider?.querySelector(".swiper");
      const pagination = slider?.querySelector("[data-tab-pagination]");
      const cursor = slider?.querySelector("[data-tab-cursor]");
      const cursorIcon = slider?.querySelector("[data-tab-cursor-icon]");
      const hotspots = slider ? Array.from(slider.querySelectorAll("[data-tab-hotspot]")) : [];

      if (!viewport) return null;

      const desktopColumns = Number(slider.dataset.columnsDesktop) || 4;
      const mobileColumns = Number(slider.dataset.columnsMobile) || 1;
      const tabletColumns = Math.max(1, Math.min(desktopColumns, 2));

      const instance = new Swiper(viewport, {
        slidesPerView: mobileColumns,
        spaceBetween: 12,
        speed: 300,
        allowTouchMove: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        pagination: pagination
          ? {
              el: pagination,
              type: "progressbar"
            }
          : false,
        breakpoints: {
          750: {
            slidesPerView: tabletColumns,
            spaceBetween: 16
          },
          990: {
            slidesPerView: desktopColumns,
            spaceBetween: 20
          }
        }
      });

      if (slider && cursor && cursorIcon) {
        slider.addEventListener("mousemove", (event) => {
          const x = event.clientX - cursor.offsetWidth / 2;
          const y = event.clientY - cursor.offsetHeight / 2;

          cursor.animate(
            { transform: `translate(${x}px, ${y}px)` },
            { duration: 100, fill: "forwards" }
          );
        });

        slider.addEventListener("mouseleave", () => {
          cursor.dataset.type = "";
          cursorIcon.className = "tabs-products__cursor-icon button button--simple";
        });

        hotspots.forEach((hotspot) => {
          hotspot.addEventListener("mouseenter", () => {
            const type = hotspot.dataset.tabHotspot || "";
            cursor.dataset.type = type;
            cursorIcon.className = `tabs-products__cursor-icon button button--simple ${type}`;
          });

          hotspot.addEventListener("mouseleave", () => {
            cursor.dataset.type = "";
            cursorIcon.className = "tabs-products__cursor-icon button button--simple";
          });

          hotspot.addEventListener("click", () => {
            if (hotspot.dataset.tabHotspot === "prev") instance.slidePrev();
            if (hotspot.dataset.tabHotspot === "next") instance.slideNext();
          });
        });
      }

      slider.classList.add("is-ready");
      return instance;
    });
  }

  onKeydown(event) {
    const currentIndex = this.triggers.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    let nextIndex = null;

    if (event.key === "ArrowRight" || event.key === "ArrowDown") nextIndex = (currentIndex + 1) % this.triggers.length;
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") nextIndex = (currentIndex - 1 + this.triggers.length) % this.triggers.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = this.triggers.length - 1;

    if (nextIndex === null) return;

    event.preventDefault();
    this.activateTab(nextIndex);
    this.triggers[nextIndex].focus();
  }

  activateTab(activeIndex) {
    this.triggers.forEach((trigger, index) => {
      const isActive = index === activeIndex;
      trigger.classList.toggle("is-active", isActive);
      trigger.setAttribute("aria-selected", isActive ? "true" : "false");
      trigger.setAttribute("tabindex", isActive ? "0" : "-1");
    });

    this.panels.forEach((panel, index) => {
      panel.classList.toggle("is-active", index === activeIndex);
      panel.hidden = index !== activeIndex;
    });

    this.updateVisibleSlider();
  }

  updateVisibleSlider() {
    this.sliders.forEach((slider, index) => {
      if (!slider) return;
      if (this.panels[index].hidden) return;
      slider.update();
    });
  }
}

if (!customElements.get("products-tabs")) {
  customElements.define("products-tabs", ProductsTabs);
}
